import { useState, useMemo, useCallback } from 'react';
import {
    GetOrganizationsQuery,
    OrganizationState,
    useGetOrganizationQuery,
    useGetOrganizationsPaginatedQuery,
} from 'data/__generated__';
import { Option, TotemSelect, TotemSelectProps } from 'components/TotemSelect';

export type OrganizationSelected = GetOrganizationsQuery['organizations'][number];

export function OrganizationSelect({
    value,
    onChange,
    dataTest,
    width,
    ...props
}: Omit<Extract<TotemSelectProps<string>, { isMulti?: false }>, 'value' | 'onChange'> & {
    value: string | null;
    onChange: (organization: string | null) => void;
}) {
    const [selectedOptionCache, setSelectedOptionCache] = useState<Option<string> | null>(null);

    // Load current organization by ID to ensure it's always available
    const { data: currentOrganizationData } = useGetOrganizationQuery({
        variables: { organizationId: value! },
        skip: !value,
    });

    const { data: organizationsData, refetch } = useGetOrganizationsPaginatedQuery({
        variables: {
            offset: 0,
            filters: {
                searchValue: '',
                state: [OrganizationState.Rollout],
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const defaultSearchOptions = useMemo(
        () =>
            (organizationsData?.organizationsFilter.organizations || []).map((organization) => ({
                label: organization.name,
                value: organization._id,
            })),
        [organizationsData],
    );

    // Build current organization option from query or cache
    const currentOrganizationOption = useMemo(() => {
        if (currentOrganizationData?.organization) {
            return {
                label: currentOrganizationData.organization.name,
                value: currentOrganizationData.organization._id,
            };
        }
        return selectedOptionCache;
    }, [currentOrganizationData, selectedOptionCache]);

    // Combine default options with current organization option to ensure it's always available
    const defaultOptions = useMemo(() => {
        if (
            currentOrganizationOption &&
            !defaultSearchOptions.find((opt) => opt.value === currentOrganizationOption.value)
        ) {
            return [currentOrganizationOption, ...defaultSearchOptions];
        }
        return defaultSearchOptions;
    }, [defaultSearchOptions, currentOrganizationOption]);

    const handleChange = (organizationId: string | null) => {
        // Cache the selected option so it persists across searches
        const option = defaultOptions.find((opt) => opt.value === organizationId) ?? null;
        if (option) {
            setSelectedOptionCache(option);
        }
        onChange(organizationId);
    };

    const fetchOptions = useCallback(
        async (query: string): Promise<Option<string>[]> => {
            const result = await refetch({
                offset: 0,
                filters: { searchValue: query, state: [OrganizationState.Rollout] },
            });
            return (result.data?.organizationsFilter.organizations || []).map((organization) => ({
                label: organization.name,
                value: organization._id,
            }));
        },
        [refetch],
    );

    return (
        <div className="min-w-[400px]">
            <TotemSelect<string>
                isClearable
                dataTest={dataTest}
                label="Choisir une organisation"
                placeholder="Sélectionner une organisation (recherche par nom ou par _id)"
                value={value}
                onChange={handleChange}
                fetchOptions={fetchOptions}
                defaultOptions={defaultOptions}
                {...props}
            />
        </div>
    );
}
